import { FormEvent, KeyboardEvent as ReactKeyboardEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { IssueImpact } from '@accestra/types';
import Button from '../ui/Button';

const MODAL_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) {
    return [];
  }

  return Array.from(container.querySelectorAll<HTMLElement>(MODAL_FOCUSABLE_SELECTOR)).filter((element) => {
    if (element.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    if (element instanceof HTMLInputElement && element.type === 'hidden') {
      return false;
    }

    return element.getClientRects().length > 0;
  });
}

function formatImpactLabel(t: (key: string) => string, impact: IssueImpact): string {
  switch (impact) {
    case 'critical':
      return t('labels.severity.critical');
    case 'serious':
      return t('labels.severity.serious');
    case 'moderate':
      return t('labels.severity.moderate');
    case 'minor':
    default:
      return t('labels.severity.minor');
  }
}

function formatReviewStatusLabel(
  t: (key: string) => string,
  status: IssueReviewDraft['workflowState'],
): string {
  switch (status) {
    case 'in_progress':
      return t('labels.workflowStates.inProgress');
    case 'fixed':
      return t('labels.workflowStates.resolved');
    case 'open':
    default:
      return t('labels.workflowStates.open');
  }
}

export interface IssueReviewModalFinding {
  issueId: string;
  code: string;
  title: string;
  impact: IssueImpact;
  wcagCriteria: string;
  wcagLevel: string;
  samplePageUrl: string | null;
  occurrenceCount: number;
}

export interface IssueReviewDraft {
  workflowState: 'open' | 'in_progress' | 'fixed';
  severityOverride: IssueImpact | '';
  note: string;
}

interface IssueReviewModalProps {
  open: boolean;
  mode: 'review' | 'edit';
  finding: IssueReviewModalFinding | null;
  draft: IssueReviewDraft;
  submitting: boolean;
  error: string | null;
  onClose(): void;
  onChange(patch: Partial<IssueReviewDraft>): void;
  onSubmit(): Promise<void>;
}

export default function IssueReviewModal({
  open,
  mode,
  finding,
  draft,
  submitting,
  error,
  onClose,
  onChange,
  onSubmit,
}: IssueReviewModalProps) {
  const { t } = useTranslation(['scanDetail', 'common']);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const statusInputRef = useRef<HTMLSelectElement | null>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
      lastFocusedElementRef.current = activeElement;
    }

    const frame = window.requestAnimationFrame(() => {
      statusInputRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [open]);

  if (!open || !finding) {
    return null;
  }

  const handleClose = () => {
    const focusTarget = lastFocusedElementRef.current;
    onClose();

    if (!focusTarget) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (focusTarget.isConnected) {
        focusTarget.focus();
      }
    });
  };

  const handleModalKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleClose();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = getFocusableElements(modalRef.current);
    if (focusableElements.length === 0) {
      event.preventDefault();
      modalRef.current?.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (!activeElement || activeElement === firstElement || !modalRef.current?.contains(activeElement)) {
        event.preventDefault();
        lastElement.focus();
      }
      return;
    }

    if (!activeElement || activeElement === lastElement || !modalRef.current?.contains(activeElement)) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onSubmit();
  };

  return (
    <div
      className="acc-form-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="acc-issue-review-title"
      aria-describedby="acc-issue-review-description"
    >
      <button
        type="button"
        className="acc-form-modal__backdrop"
        onClick={handleClose}
        aria-label={t('actions.cancel')}
      />

      <div
        ref={modalRef}
        className="acc-form-modal__panel"
        onKeyDown={handleModalKeyDown}
      >
        <div className="acc-form-modal__eyebrow">{t('labels.review')}</div>
        <h2 id="acc-issue-review-title" className="acc-form-modal__title">
          {t(mode === 'edit' ? 'labels.editReviewTitle' : 'labels.reviewTitle')}
        </h2>
        <p id="acc-issue-review-description" className="acc-form-modal__description">
          {t('labels.reviewDescription')}
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 14 }}>
          <div className="acc-form-modal__hint" style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'grid', gap: 3 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('labels.findingContext')}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--acc-color-text)' }}>
                {finding.title}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
              <div>
                <div className="acc-form-modal__label">{t('labels.rule')}</div>
                <div style={{ fontSize: 13, color: 'var(--acc-color-text)', lineHeight: 1.5 }}>{finding.code}</div>
              </div>
              <div>
                <div className="acc-form-modal__label">{t('labels.wcagReference')}</div>
                <div style={{ fontSize: 13, color: 'var(--acc-color-text)', lineHeight: 1.5 }}>
                  WCAG {finding.wcagLevel} {finding.wcagCriteria}
                </div>
              </div>
              <div>
                <div className="acc-form-modal__label">{t('labels.sourceSeverity')}</div>
                <div style={{ fontSize: 13, color: 'var(--acc-color-text)', lineHeight: 1.5 }}>
                  {formatImpactLabel(t, finding.impact)}
                </div>
              </div>
              <div>
                <div className="acc-form-modal__label">{t('labels.occurrenceCount')}</div>
                <div style={{ fontSize: 13, color: 'var(--acc-color-text)', lineHeight: 1.5 }}>
                  {finding.occurrenceCount}
                </div>
              </div>
            </div>
            <div>
              <div className="acc-form-modal__label">{t('labels.sampleRoute')}</div>
              <div style={{ fontSize: 13, color: 'var(--acc-color-text)', lineHeight: 1.6, wordBreak: 'break-word' }}>
                {finding.samplePageUrl ?? '—'}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <label className="acc-form-modal__field">
              <span className="acc-form-modal__label">{t('labels.reviewStatus')}</span>
              <select
                ref={statusInputRef}
                value={draft.workflowState}
                onChange={(event) => onChange({
                  workflowState: event.target.value as IssueReviewDraft['workflowState'],
                })}
                className="acc-select"
                style={{ background: 'var(--input-bg)', color: 'var(--text)', colorScheme: 'inherit' }}
              >
                <option value="open">{formatReviewStatusLabel(t, 'open')}</option>
                <option value="in_progress">{formatReviewStatusLabel(t, 'in_progress')}</option>
                <option value="fixed">{formatReviewStatusLabel(t, 'fixed')}</option>
              </select>
            </label>

            <label className="acc-form-modal__field">
              <span className="acc-form-modal__label">{t('labels.severityOverride')}</span>
              <select
                value={draft.severityOverride}
                onChange={(event) => onChange({
                  severityOverride: event.target.value as IssueReviewDraft['severityOverride'],
                })}
                className="acc-select"
                style={{ background: 'var(--input-bg)', color: 'var(--text)', colorScheme: 'inherit' }}
              >
                <option value="">{t('labels.useScannerSeverity')}</option>
                <option value="critical">{formatImpactLabel(t, 'critical')}</option>
                <option value="serious">{formatImpactLabel(t, 'serious')}</option>
                <option value="moderate">{formatImpactLabel(t, 'moderate')}</option>
                <option value="minor">{formatImpactLabel(t, 'minor')}</option>
              </select>
              <span style={{ fontSize: 11, color: 'var(--acc-color-text-muted)', lineHeight: 1.5 }}>
                {t('labels.severityOverrideHint')}
              </span>
            </label>
          </div>

          <label className="acc-form-modal__field">
            <span className="acc-form-modal__label">{t('labels.analystNotes')}</span>
            <textarea
              className="acc-input"
              value={draft.note}
              onChange={(event) => onChange({ note: event.target.value })}
              placeholder={t('labels.analystNotesPlaceholder')}
              rows={5}
              maxLength={5000}
              style={{ minHeight: 132, resize: 'vertical', background: 'var(--input-bg)', color: 'var(--text)' }}
            />
          </label>

          {error && (
            <div role="alert" style={{ color: 'var(--acc-color-danger)', fontSize: 13 }}>
              {error}
            </div>
          )}

          <div className="acc-form-modal__actions">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('actions.savingReview') : t('actions.saveReview')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
